import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Shell, Field, PasswordField } from "@/pages/Login";

export default function InviteAccept() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [ack, setAck] = useState(false);
  const [loading, setLoading] = useState(false);
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/invites/info/${code}`).then((r) => setInfo(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match. Please re-enter them.");
    if (info.existing_account && !ack) return toast.error("Please confirm you understand your existing account will be reset.");
    setLoading(true);
    try {
      const { data } = await api.post("/invites/accept", { code, name, password });
      applyAuth(data);
      toast.success("You're in!");
      navigate("/studio");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not accept invite");
    } finally {
      setLoading(false);
    }
  };

  if (error) return <Shell title="Invite unavailable"><p className="text-sm text-[var(--ink-soft)]">{error}</p></Shell>;
  if (!info) return <Shell title="Loading invite…"><p className="text-sm text-[var(--ink-soft)]">Please wait…</p></Shell>;

  return (
    <Shell title={`Join ${info.company_name}`} subtitle={`Invited as ${info.role.replace("_", " ")} · ${info.email}`}>
      {info.existing_account && (
        <div data-testid="existing-account-warning" className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex gap-2">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" strokeWidth={1.75} />
            <div>
              <p className="text-sm font-semibold text-amber-900">This email already has an account</p>
              <p className="mt-1 text-xs leading-relaxed text-amber-800">
                Accepting this invite will <strong>delete and reset your existing account and its data</strong>, then move you
                into <strong>{info.company_name}</strong> as a {info.role.replace("_", " ")}. This can't be undone.
              </p>
              <label className="mt-3 flex items-start gap-2 text-xs font-medium text-amber-900">
                <input type="checkbox" data-testid="reset-ack-checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
                I understand my old account will be reset.
              </label>
            </div>
          </div>
        </div>
      )}
      <form onSubmit={submit}>
        <Field label="Your name" required value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
        <PasswordField label="Create a password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} testid="invite-password" />
        <PasswordField label="Confirm password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} testid="invite-confirm-password" />
        <button
          type="submit"
          disabled={loading || (info.existing_account && !ack)}
          data-testid="invite-submit"
          className="mt-2 w-full rounded-full bg-[var(--brand)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60"
        >
          {loading ? "Joining…" : "Accept invite"}
        </button>
      </form>
    </Shell>
  );
}
